import { accountApi } from "./api/accountApi";
import type {
  PasskeyRegisterOptions,
  SerializedAttestation,
} from "../webAuthn";

/**
 * Passkey (WebAuthn) enrollment & management against account-service. Injected
 * into the app's `accountApi` so it shares the same `account` base query and the
 * session cookie. Used by the Passkeys settings screen.
 */

export type PasskeyCredential = {
  id: number;
  credentialID: string;
  label: string;
  createdAt: string;
  lastUsedAt?: string;
};

type PasskeyRegisterOptionsResponse = {
  flowId: string;
  options: PasskeyRegisterOptions;
};

type PasskeyRegisterVerifyBody = {
  flowId: string;
  label: string;
  credential: SerializedAttestation;
};

export const passkeyEndpoints = accountApi.injectEndpoints({
  endpoints: (build) => ({
    passkeys: build.query<PasskeyCredential[], void>({
      query: () => "users/passkeys",
      // The endpoint returns null (not []) when the user has none.
      transformResponse: (response: PasskeyCredential[] | null) =>
        response ?? [],
      providesTags: ["passkeys"],
    }),
    passkeyRegisterOptions: build.mutation<
      PasskeyRegisterOptionsResponse,
      void
    >({
      query: () => ({
        method: "POST",
        url: "users/passkey/register/options",
      }),
    }),
    passkeyRegisterVerify: build.mutation<unknown, PasskeyRegisterVerifyBody>({
      query: (body) => ({
        method: "POST",
        body,
        url: "users/passkey/register/verify",
      }),
      invalidatesTags: ["passkeys"],
    }),
    deletePasskey: build.mutation<unknown, string>({
      query: (credentialID) => ({
        method: "DELETE",
        url: `users/passkeys/${credentialID}`,
      }),
      invalidatesTags: ["passkeys"],
    }),
  }),
});

export const {
  usePasskeysQuery,
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
  useDeletePasskeyMutation,
} = passkeyEndpoints;

export default passkeyEndpoints;
